/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    AUTO_LOGOUT_ON_401
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Global axios response interceptor that automatically handles 401 Unauthorized errors
 * by clearing invalid tokens and forcing users to re-login. This handles cases where:
 * - JWTKEY is changed on the server
 * - Tokens expire
 * - Tokens become invalid for any reason
 *
 * Also handles 400 errors that contain authentication-related messages as a defensive
 * fallback (in case server returns 400 instead of 401 for auth failures).
 *
 * Related Screens / Modules:
 * - client/src/utils/axiosInterceptor.ts
 * - client/src/index.tsx
 * - client/src/utils/authUtils.ts
 * - server/middlewares/auth.js
 */

import axios from "axios";
import { clearToken, logoutUser } from "./authUtils";

// Flag to prevent multiple simultaneous redirects
let isRedirecting = false;

// [2026-07-06] GUEST_ACCESS_PHASE1: when the visitor is an anonymous guest, a 401 is
// EXPECTED on any page (they have no session). App.tsx sets this after the auth check;
// while true the interceptor never hard-redirects to /signin.
let guestMode = false;
export function setGuestMode(value: boolean): void {
  guestMode = value;
}

/**
 * Auth endpoints that should NOT trigger auto-logout
 * These endpoints legitimately return 401 for failed login attempts
 */
const AUTH_ENDPOINTS = [
  "/api/auth/login",
  "/api/auth/verify",
  "/api/auth/password/forgot",
  "/api/auth/password/reset",
  "/api/auth/google",
  "/api/users/auth/me", // [2026-02-11] Cookie auth: Auth status check
  "/api/users/login",
  "/api/users/auth/google",
];

/**
 * Auth pages where we should NOT redirect (to prevent loops)
 */
const AUTH_PAGES = ["/signin", "/signup", "/forgot", "/reset-password", "/verify-email"];

// [2026-07-05] GUEST_ACCESS_PHASE1: public guest routes — a 401 here is EXPECTED (guests
// have no session; e.g. Navbar getUserProfile 401s). Never hard-redirect guests to /signin
// from these pages, or the public landing (BR-01/BR-06) is impossible.
const PUBLIC_ROUTES = ["/", "/search"];

/**
 * Check if an error response indicates an authentication failure
 * This includes 401 errors and 400 errors with auth-related messages
 */
function isAuthenticationError(error: any): boolean {
  if (!axios.isAxiosError(error) || !error.response) {
    return false;
  }

  const status = error.response.status;
  const responseData = error.response.data;

  // 401 is always an authentication error
  if (status === 401) {
    return true;
  }

  // Check if 400 error contains authentication-related messages
  if (status === 400) {
    // Convert response data to string for checking
    let errorMessage = "";
    if (typeof responseData === "string") {
      errorMessage = responseData.toLowerCase();
    } else if (responseData?.error) {
      errorMessage = String(responseData.error).toLowerCase();
    } else if (responseData?.message) {
      errorMessage = String(responseData.message).toLowerCase();
    }

    // Check for authentication-related keywords
    const authKeywords = [
      "invalid signature",
      "jwt",
      "token",
      "jsonwebtokenerror",
      "tokenexpirederror",
      "notbeforeerror",
      "invalid token",
      "expired token",
      "malformed token",
    ];

    return authKeywords.some((keyword) => errorMessage.includes(keyword));
  }

  return false;
}

/**
 * Initialize axios response interceptor for automatic logout on 401 errors
 * Also handles 400 errors with authentication-related messages as a fallback
 * This should be called once during app initialization
 */
export function setupAxiosInterceptor(): void {
  axios.interceptors.response.use(
    (response) => {
      // Pass through successful responses
      return response;
    },
    (error) => {
      // Handle 401 Unauthorized errors and 400 errors with auth-related messages
      if (isAuthenticationError(error)) {
        // Check if already redirecting to prevent multiple redirects
        if (isRedirecting) {
          return Promise.reject(error);
        }

        // Get the request URL to check if it's an auth endpoint
        const requestUrl = error.config?.url || "";
        const isAuthEndpoint = AUTH_ENDPOINTS.some((endpoint) =>
          requestUrl.includes(endpoint),
        );

        // Don't auto-logout for auth endpoints (login failures, etc.)
        if (isAuthEndpoint) {
          return Promise.reject(error);
        }

        // [2026-07-06] GUEST_ACCESS_PHASE1: guests have no session — never redirect them.
        if (guestMode) {
          return Promise.reject(error);
        }

        // Check if already on an auth page (don't redirect to prevent loops)
        // [2026-07-05] GUEST_ACCESS_PHASE1: also skip on public guest routes — a 401 there
        // is expected (guest has no session) and must NOT bounce them to /signin.
        const currentPath = window.location.pathname;
        if (AUTH_PAGES.includes(currentPath) || PUBLIC_ROUTES.includes(currentPath)) {
          return Promise.reject(error);
        }

        // [2026-02-11] Cookie auth: Clear both cookie and localStorage
        // Use logoutUser() to call server logout endpoint (clears cookie)
        // Also clear localStorage for backward compatibility
        clearToken();

        // Set redirecting flag to prevent multiple redirects
        isRedirecting = true;

        // Show a brief message before redirect
        // Using console.warn since toast notifications might not be ready
        console.warn("Session expired. Redirecting to login...");

        // Call logout endpoint to clear cookie, then redirect
        logoutUser().finally(() => {
          // Redirect to signin page
          // Using window.location.href for a full page reload to ensure clean state
          window.location.href = "/signin";
        });

        return Promise.reject(error);
      }

      // For all other errors (non-authentication), pass through unchanged
      return Promise.reject(error);
    },
  );
}

/**
 * Reset the redirecting flag (useful for testing or manual resets)
 */
export function resetRedirectFlag(): void {
  isRedirecting = false;
}

